import type { GameState, ScoreBreakdown } from '../game/types.ts'
import { Board } from './Board.tsx'
import { ScorePanel } from './ScorePanel.tsx'
import { UndoButton } from './UndoButton.tsx'
import { DIFFICULTIES } from '../config/ai-difficulty.ts'
import bg from '../assets/bg2.webp'
import { tx } from '../i18n/translations.ts'

/**
 * 対戦モード。現状は NPC 戦のみ実装されており、マルチは Phase 4 で実装予定。
 * Undo は現行ゲームデザインでは広告報酬にしないため非表示。
 */
export type GameMode = 'npc' | 'multi'

interface Props {
  state: GameState
  blackScore: ScoreBreakdown
  whiteScore: ScoreBreakdown
  highscore: number
  onCellClick: (row: number, col: number) => void
  onPopupComplete: (id: number) => void
  // 対戦モード。省略時は 'npc'（既存互換）。
  mode?: GameMode
  // Undo 関連 props（mode='npc' かつ UndoButton 表示時のみ意味を持つ）
  undoIsUnlocked: boolean
  undoRemainingUses: number
  undoIsUnlocking: boolean
  undoCanUndo: boolean
  onUndoUnlock: () => void
  onUndo: () => void
}

export function GameScreen({
  state,
  blackScore,
  whiteScore,
  highscore,
  onCellClick,
  onPopupComplete,
  undoIsUnlocked,
  undoRemainingUses,
  undoIsUnlocking,
  undoCanUndo,
  onUndoUnlock,
  onUndo,
}: Props) {
  return (
    <div
      className="screen screen--game"
      style={{ backgroundImage: `url(${bg})` }}
    >
      {/* スコアパネル（リアルタイム表示） */}
      <ScorePanel
        blackScore={blackScore}
        whiteScore={whiteScore}
        turn={state.turn}
        playerColor={state.playerColor}
        highscore={highscore}
      />

      {/* 盤面 */}
      <Board
        board={state.board}
        territory={state.territory}
        winLine={state.winLine}
        flowers={state.flowers}
        map={state.map}
        turnCount={state.turnCount}
        turn={state.turn}
        playerColor={state.playerColor}
        phase={state.phase}
        finaleHighlight={state.finaleHighlight}
        finaleSparkle={state.finaleKind === 'win'}
        scorePopups={state.scorePopups}
        onCellClick={onCellClick}
        onPopupComplete={onPopupComplete}
      />

      {/* 手数 + 難易度表示 */}
      <div className="turn-count">
        {tx('turnCount', {
          count: state.turnCount,
          difficulty: DIFFICULTIES[state.difficulty].label,
        })}
      </div>

      {/* Undo 広告導線はゲーム体験を崩すため無効化。互換のため slot だけ残す。 */}
      <div className="undo-slot">
        <UndoButton
          visible={false}
          isUnlocked={undoIsUnlocked}
          remainingUses={undoRemainingUses}
          isUnlocking={undoIsUnlocking}
          canUndo={undoCanUndo}
          onUnlock={onUndoUnlock}
          onUndo={onUndo}
        />
      </div>
    </div>
  )
}
